import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Moon, Sun, Palette, Globe, Trash2, User, Check, CreditCard, Calendar, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { useTheme, THEMES } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

export const SettingsPage = () => {
  const { currentTheme, changeTheme, themes } = useTheme();
  const { user } = useAuth();
  const { projects, deleteProject } = useProject();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const subscription = useMemo(() => {
    if (!user) return null;
    const expiresAt = user.plan_expires_at ? new Date(user.plan_expires_at) : null;
    const now = new Date();
    const isActive = user.plan && expiresAt && expiresAt > now;
    const daysLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24))) : 0;
    const planName = user.plan === 'monthly' ? 'Mensual' : user.plan === 'annual' ? 'Anual' : null;
    return { isActive, expiresAt, daysLeft, planName };
  }, [user]);

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-4xl font-bold mb-2">Configuración</h1>
        <p className="text-muted-foreground">
          Personaliza tu experiencia en Baluad
        </p>
      </div>

      {/* Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Perfil
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center text-2xl font-bold text-primary">
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="font-semibold">{user?.name || 'Usuario'}</p>
              <p className="text-sm text-muted-foreground">{user?.email || ''}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscription */}
      <Card className={subscription?.isActive ? 'border-green-500/30' : 'border-amber-500/30'}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Suscripción
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {subscription?.isActive ? (
            <>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                <ShieldCheck className="w-5 h-5 text-green-400" />
                <div>
                  <p className="font-semibold text-green-400">Plan Activo — {subscription.planName}</p>
                  <p className="text-sm text-muted-foreground">
                    {subscription.daysLeft > 0 
                      ? `${subscription.daysLeft} día${subscription.daysLeft !== 1 ? 's' : ''} restantes`
                      : 'Expira hoy'}
                    {subscription.expiresAt && ` (vence ${subscription.expiresAt.toLocaleDateString('es-MX')})`}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" size="sm" onClick={() => navigate('/pricing')}>
                  <Calendar className="w-4 h-4 mr-2" />
                  Cambiar Plan
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <div>
                  <p className="font-semibold text-amber-400">Sin suscripción activa</p>
                  <p className="text-sm text-muted-foreground">Activa un plan para guardar proyectos y usar el asistente IA</p>
                </div>
              </div>
              <Button onClick={() => navigate('/pricing')} className="bg-gradient-to-r from-blue-500 to-cyan-500">
                <CreditCard className="w-4 h-4 mr-2" />
                Ver Planes
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Theme Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Tema de la Aplicación
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground mb-4">
            Selecciona el esquema de colores para toda la aplicación
          </p>
          
          <div className="grid grid-cols-2 gap-3">
            {Object.values(THEMES).map((theme) => (
              <button
                key={theme.key}
                onClick={() => changeTheme(theme.key)}
                className={cn(
                  "relative p-4 rounded-lg border-2 transition-all text-left",
                  currentTheme === theme.key 
                    ? "border-primary bg-primary/10" 
                    : "border-border hover:border-primary/50"
                )}
              >
                <div className="flex items-center gap-3">
                  {/* Color Preview */}
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ 
                      backgroundColor: theme.key === 'light' ? '#f8fafc' : 
                        theme.key === 'blue' ? '#0c1929' :
                        theme.key === 'emerald' ? '#0a1612' :
                        theme.key === 'purple' ? '#13091f' : '#0d1117'
                    }}
                  >
                    {theme.key === 'light' ? (
                      <Sun className="w-5 h-5 text-amber-500" />
                    ) : (
                      <Moon className="w-5 h-5 text-slate-300" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold">{theme.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {theme.key === 'light' ? 'Fondo claro' : 'Fondo oscuro'}
                    </p>
                  </div>
                </div>
                
                {/* Selected indicator */}
                {currentTheme === theme.key && (
                  <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                    <Check className="w-3 h-3 text-primary-foreground" />
                  </div>
                )}
                
                {/* Color bar preview */}
                <div className="flex gap-1 mt-3">
                  <div 
                    className="h-2 flex-1 rounded"
                    style={{ 
                      backgroundColor: theme.key === 'light' ? '#3b82f6' :
                        theme.key === 'blue' ? '#0ea5e9' :
                        theme.key === 'emerald' ? '#10b981' :
                        theme.key === 'purple' ? '#8b5cf6' : '#3b82f6'
                    }}
                  />
                  <div 
                    className="h-2 flex-1 rounded"
                    style={{ 
                      backgroundColor: theme.key === 'light' ? '#10b981' :
                        theme.key === 'blue' ? '#38bdf8' :
                        theme.key === 'emerald' ? '#34d399' :
                        theme.key === 'purple' ? '#a78bfa' : '#10b981'
                    }}
                  />
                  <div 
                    className="h-2 flex-1 rounded"
                    style={{ 
                      backgroundColor: theme.key === 'light' ? '#f59e0b' :
                        theme.key === 'blue' ? '#22d3ee' :
                        theme.key === 'emerald' ? '#6ee7b7' :
                        theme.key === 'purple' ? '#c4b5fd' : '#f59e0b'
                    }}
                  />
                </div>
              </button>
            ))}
          </div>
          
          <p className="text-xs text-muted-foreground mt-2">
            Nota: Los cambios se aplican inmediatamente y se guardan automáticamente.
          </p>
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Preferencias
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Moneda Predeterminada</Label>
              <p className="text-sm text-muted-foreground">Moneda para nuevos proyectos</p>
            </div>
            <Select defaultValue="MXN">
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MXN">MXN</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="w-5 h-5" />
            Zona de Peligro
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-destructive">Eliminar todos los datos</Label>
              <p className="text-sm text-muted-foreground">Se eliminaran todos tus proyectos permanentemente. Esta accion no se puede deshacer.</p>
            </div>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => setDeleteDialogOpen(true)}
              data-testid="delete-all-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar Todo
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => { setDeleteDialogOpen(open); if (!open) setDeleteConfirmText(''); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Confirmar Eliminacion Total
            </DialogTitle>
            <DialogDescription>
              Esta accion eliminara <strong>todos tus proyectos</strong> ({projects.length} proyecto{projects.length !== 1 ? 's' : ''}) de forma permanente. No se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <p className="text-sm text-destructive font-medium">
                Para confirmar, escribe "ELIMINAR" en el campo de abajo:
              </p>
            </div>
            <Input
              placeholder='Escribe "ELIMINAR" para confirmar'
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="text-center font-mono tracking-widest"
              data-testid="delete-confirm-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmText(''); }}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={deleteConfirmText !== 'ELIMINAR' || deleting}
              onClick={async () => {
                setDeleting(true);
                try {
                  for (const p of projects) {
                    await deleteProject(p.id);
                  }
                  toast.success('Todos los proyectos han sido eliminados');
                  setDeleteDialogOpen(false);
                  setDeleteConfirmText('');
                } catch (err) {
                  toast.error('Error al eliminar los proyectos');
                } finally {
                  setDeleting(false);
                }
              }}
              data-testid="delete-confirm-btn"
            >
              {deleting ? 'Eliminando...' : 'Eliminar Todo Permanentemente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
